// Today — the daily plan of action.
//
// Every line is computed from the database; nothing here is written by
// a model. That is worth knowing while reading the page, and it is why
// each item shows the fact that put it there rather than a summary of
// one.
//
// The page is deliberately plain. No severity colours, no red badges:
// the only threshold in play is the firm's own look-ahead setting, and
// inventing a palette on top of it would assert a judgment the app has
// not earned. Order carries the priority; the numbers speak for
// themselves.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";
import { SignalRow, KIND_LABELS } from "../components/SignalRow.jsx";
import { CoveragePanel } from "../components/CoveragePanel.jsx";

export function TodayPage({ onMatterClick, embedded = false }) {
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setBrief(await apiFetch("/api/daily-brief"));
    } catch (e) {
      setError(e.message);
      setBrief({ people: [], totals: {}, coverage: {} });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (brief === null) {
    return React.createElement("div", {
      style: { display: "flex", gap: "8px", alignItems: "center", padding: "40px 0" },
    },
      React.createElement(Spinner, { size: 16 }),
      React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
        "Working out what needs doing…"));
  }

  const totals = brief.totals || {};
  const coverage = brief.coverage || {};

  // The content wrapper in app.jsx sets a left margin for the sidebar
  // but no horizontal padding, so a page with none of its own runs
  // straight into the nav. Matches PipelineDashboardPage.
  // Embedded inside My Day's "Whole firm" scope, the page chrome
  // belongs to the host — two headings and two sets of gutters read as
  // a page inside a page.
  return React.createElement("div", {
    style: embedded ? null : { padding: "32px 40px", maxWidth: "900px" },
  },
    !embedded && React.createElement("h1", {
      style: { fontFamily: "'Playfair Display', serif", fontSize: "32px",
               color: C.navy, marginBottom: "6px" },
    }, "Today"),
    !embedded && React.createElement("p", {
      style: { fontSize: "14px", color: C.textMuted, marginBottom: "18px",
               lineHeight: 1.55 },
    }, `What needs attention, by person. Deadlines past due or within `
     + `${brief.horizon_days} days, plus work waiting on a decision.`),

    error && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px" },
    }, error),

    // Anything the app could not read. Stated up front rather than
    // tucked at the bottom, because a short brief and a broken one look
    // identical otherwise.
    (coverage.degraded || []).length > 0 && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px", lineHeight: 1.5 },
    },
      React.createElement("strong", null, "This brief is incomplete. "),
      coverage.degraded.join(" "),
    ),

    // Counts, stated plainly.
    React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", gap: "22px", flexWrap: "wrap",
               padding: "12px 0 18px", borderBottom: `1px solid ${C.warmGray}`,
               marginBottom: "22px" },
    },
      ...[
        ["Items", totals.signals],
        ["Past due", totals.past_due],
        ["Jurisdictional", totals.jurisdictional],
        ["Unassigned", totals.unassigned],
      ].map(([label, n]) => React.createElement("div", { key: label },
        React.createElement("div", {
          style: { fontSize: "22px", fontWeight: 700, color: C.navy,
                   fontFamily: "'Playfair Display',serif",
                   fontVariantNumeric: "tabular-nums" },
        }, n == null ? "—" : String(n)),
        React.createElement("div", {
          style: { fontSize: "11px", color: C.textMuted,
                   textTransform: "uppercase", letterSpacing: "0.06em" },
        }, label),
      )),
    ),

    (brief.people.length === 0 || (totals.signals || 0) === 0)
      ? React.createElement("div", {
          style: { background: "#fff", border: `1px solid ${C.warmGray}`,
                   borderRadius: "12px", padding: "28px", fontSize: "14px",
                   color: C.textMuted, lineHeight: 1.6 },
        },
          // "You are up to date" and "nothing has ever been recorded
          // here" produce an identical empty page, and only one of them
          // is reassuring. Saying which is the whole obligation.
          brief.no_records_anywhere
            ? React.createElement("div", null,
                React.createElement("div", {
                  style: { color: C.textDark, fontWeight: 600, marginBottom: "8px" },
                }, "There is nothing here to draw on yet."),
                React.createElement("div", null,
                  "Every source this brief reads is empty — not quiet, "
                  + "empty. That is not the same as being up to date, and "
                  + "nothing below should be read as a deadline having "
                  + "been checked."),
                React.createElement("div", { style: { marginTop: "10px" } },
                  "These fill in as matters are worked through the app:"),
                React.createElement("ul", { style: { margin: "6px 0 0 18px", padding: 0 } },
                  ...(brief.coverage?.sources || [])
                    .filter((s) => s.populated_by)
                    .map((s, i) => React.createElement("li", { key: i },
                      s.label, " — starts with ", s.populated_by, ".")),
                ),
              )
            : "Nothing is past due or falling due in the window, and "
              + "nothing is waiting on a decision. This is a summary, not "
              + "a docket — check the deadline list for the full picture.")
      : React.createElement("div", null,
          ...brief.people.map((p) => React.createElement(PersonBlock, {
            key: p.person || "__unassigned__",
            block: p,
            unassignedTo: brief.unassigned_to,
            onMatterClick,
          })),
        ),

    React.createElement(CoveragePanel, { coverage }),
  );
}


function PersonBlock({ block, unassignedTo, onMatterClick }) {
  const trimmed = Object.entries(block.trimmed || {});
  const title = block.is_unassigned ? "Unassigned" : block.person;

  return React.createElement("div", { style: { marginBottom: "30px" } },
    React.createElement("div", {
      style: { display: "flex", alignItems: "baseline", gap: "10px",
               marginBottom: "10px" },
    },
      React.createElement("h2", {
        style: { fontSize: "13px", color: C.navy, fontWeight: 700,
                 textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 },
      }, title),
      React.createElement("div", { style: { flex: 1, height: "1px", background: C.warmGray } }),
      React.createElement("span", {
        style: { fontSize: "11px", color: C.textLight },
      },
        // Matter count first. Without it a row reading "0 items" looks
        // like an empty person rather than somebody whose 62 matters
        // happen to need nothing today.
        block.counts?.matters
          ? `${block.counts.matters} matter${block.counts.matters === 1 ? "" : "s"} · `
          : "",
        `${block.total_signals} item${block.total_signals === 1 ? "" : "s"}`),
    ),

    block.is_unassigned && React.createElement("p", {
      style: { fontSize: "12px", color: C.textMuted, marginTop: 0,
               marginBottom: "10px", lineHeight: 1.5 },
    }, unassignedTo === "nobody"
        ? "Nobody owns these. They are on this list and no one else's."
        : `Nobody owns these — they fall to ${unassignedTo === "firm_admin"
            ? "firm admins" : "partners"} until someone is assigned.`),

    React.createElement("div", {
      style: { background: "#fff", border: `1px solid ${C.warmGray}`,
               borderRadius: "10px", overflow: "hidden" },
    },
      ...block.signals.map((s, i) => React.createElement(SignalRow, {
        key: s.id, signal: s, first: i === 0, onMatterClick,
      })),
    ),

    trimmed.length > 0 && React.createElement("p", {
      style: { fontSize: "11.5px", color: C.textMuted, marginTop: "8px",
               lineHeight: 1.5 },
    }, "Not shown, to keep this readable: "
     + trimmed.map(([kind, n]) =>
         `${n} ${(KIND_LABELS[kind] || kind).toLowerCase()}`).join(", ")
     + ". Statutory dates are never held back.",
    ),

    (block.done_yesterday || []).length > 0 && React.createElement("div", {
      style: { marginTop: "12px" },
    },
      React.createElement("div", {
        style: { fontSize: "11px", fontWeight: 600, color: C.textMuted,
                 textTransform: "uppercase", letterSpacing: "0.05em",
                 marginBottom: "5px" },
      }, "Yesterday"),
      ...block.done_yesterday.slice(0, 6).map((d, i) =>
        React.createElement("div", {
          key: i,
          style: { fontSize: "12px", color: C.textMuted, lineHeight: 1.6 },
        }, d.description,
          d.matter_label && React.createElement("span", {
            style: { color: C.textLight, marginLeft: "6px" },
          }, d.matter_label),
        )),
    ),
  );
}
